/**
 * Crew Service
 *
 * Lightweight wrapper for managing "Sub-teams" (crews) like Crew A / Crew B.
 *
 * Data lives in `localStorage.maidly_crews` and is auto-pushed to the cloud
 * by AutoSync (no extra backend wiring needed — AutoSync intercepts every
 * write to `maidly_*` keys).
 *
 * Each crew:
 *   { id, name, color, created_at, updated_at }
 *
 * Active crew filter (per-device) lives in `localStorage.maidly_active_crew_filter`.
 *   - value === ''        → "All Crews" (admin/manager default)
 *   - value === <crewId>  → only show that crew's data
 */

const CREWS_KEY = 'maidly_crews';
const ACTIVE_FILTER_KEY = 'maidly_active_crew_filter';

// Soft, distinct colors that work on both light cards and dark calendar pills.
export const CREW_COLOR_PALETTE = [
  { name: 'Blue', value: '#0073ea' },
  { name: 'Green', value: '#00c875' },
  { name: 'Orange', value: '#fdab3d' },
  { name: 'Purple', value: '#a25ddc' },
  { name: 'Pink', value: '#f5576c' },
  { name: 'Teal', value: '#0ea5e9' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Slate', value: '#64748b' },
];

export const DEFAULT_CREW_COLOR = CREW_COLOR_PALETTE[0].value;

export function listCrews() {
  try {
    const raw = localStorage.getItem(CREWS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function saveCrews(crews) {
  localStorage.setItem(CREWS_KEY, JSON.stringify(crews));
}

export function getCrew(crewId) {
  if (!crewId) return null;
  return listCrews().find((c) => c.id === crewId) || null;
}

export function getCrewColor(crewId) {
  return getCrew(crewId)?.color || '#9ca3af';
}

export function getCrewName(crewId) {
  return getCrew(crewId)?.name || '';
}

export function createCrew({ name, color }) {
  const crews = listCrews();
  const now = new Date().toISOString();
  const id = `crew_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
  const next = [
    ...crews,
    {
      id,
      name: (name || '').trim(),
      color: color || DEFAULT_CREW_COLOR,
      created_at: now,
      updated_at: now,
    },
  ];
  saveCrews(next);
  return next[next.length - 1];
}

export function updateCrew(crewId, patch) {
  const crews = listCrews();
  const next = crews.map((c) =>
    c.id === crewId
      ? { ...c, ...patch, updated_at: new Date().toISOString() }
      : c
  );
  saveCrews(next);
}

export function deleteCrew(crewId) {
  saveCrews(listCrews().filter((c) => c.id !== crewId));

  // Unassign this crew from any employees / customers so we don't leave
  // dangling references.
  ['maidly_employees', 'maidly_customers'].forEach((key) => {
    try {
      const items = JSON.parse(localStorage.getItem(key) || '[]');
      const cleaned = items.map((it) =>
        it && it.crewId === crewId ? { ...it, crewId: '' } : it
      );
      localStorage.setItem(key, JSON.stringify(cleaned));
    } catch (e) {
      // Storage key holds non-JSON or unreadable shape — skip this dataType
      // rather than aborting the whole detach-from-crew operation.
      console.warn('[CrewService] failed to scrub crewId from', key, e);
    }
  });
}

/**
 * Active filter helpers — used by every page that supports crew filtering.
 * The filter is per-device (not per-user, not synced) so each device can show
 * a different scope.
 */
export function getActiveCrewFilter() {
  return localStorage.getItem(ACTIVE_FILTER_KEY) || '';
}

export function setActiveCrewFilter(crewId) {
  if (crewId) {
    localStorage.setItem(ACTIVE_FILTER_KEY, crewId);
  } else {
    localStorage.removeItem(ACTIVE_FILTER_KEY);
  }
  // Notify all CrewFilter components on the page so they re-render.
  window.dispatchEvent(new CustomEvent('maidly:crew-filter-changed', { detail: { crewId } }));
}

/**
 * For employees, the "effective" filter is always THEIR crew (no choice).
 * For admins/managers, the effective filter is whatever they picked
 * (defaulting to "All").
 */
export function getEffectiveCrewFilter(currentUser) {
  if (!currentUser) return '';
  const role = currentUser.role;
  if (role === 'employee') {
    // Look up their crewId from the employees list.
    try {
      const employees = JSON.parse(localStorage.getItem('maidly_employees') || '[]');
      const me = employees.find(
        (e) => e.email === currentUser.email || e.id === currentUser.id
      );
      return me?.crewId || '';
    } catch {
      return '';
    }
  }
  return getActiveCrewFilter();
}

/**
 * Crew-id of a list of employee IDs. Returns the crewId if all share one,
 * or '' if they're mixed. Used to color schedule appointments.
 */
export function crewIdForEmployeeIds(employeeIds = []) {
  if (!employeeIds || employeeIds.length === 0) return '';
  try {
    const all = JSON.parse(localStorage.getItem('maidly_employees') || '[]');
    const crewIds = employeeIds
      .map((id) => all.find((e) => e.id === id)?.crewId)
      .filter(Boolean);
    if (crewIds.length === 0) return '';
    const first = crewIds[0];
    return crewIds.every((c) => c === first) ? first : '';
  } catch {
    return '';
  }
}
